import { useContext, useEffect, useState } from "react";
import { PaymentTerms } from "../../types";
import Picker from "../widgets/components/SimplePicker";
import { OptionType } from "../masterdata/components/AttributePicker";
import { deletePaymentTerm, fetchPaymentTerms, upSertPayment, upSertPaymentTerm } from "../../api";
import { ToolbarContext, defaultToolbarState } from "../../../_myb/layout/components/toolbar/Toolbar";
import { Column } from "react-table";
import React from "react";
import { KTIcon } from "../../../_myb/helpers";
import { Content } from "../../../_myb/layout/components/content";
import { DataTable } from "../widgets/components/DataTable";
import CustomModal from "../widgets/components/CustomModal";
import { toast } from "react-toastify";


interface PaymentTermsPickerProp {
  selectedValue?: string;
  label?: string;
  onSelectionChange: (paymentTerms: string) => void;
  // NEW: Props for centralized default management
  defaultValue?: string; // Default payment term from parent
  onSaveAsDefault?: (paymentTermId: string) => Promise<boolean>; // Callback to save default
  showDefaultOption?: boolean; // Whether to show "Keep as default" option
}

export const PaymentTermsPicker: React.FC<PaymentTermsPickerProp> = ({ 
  selectedValue, 
  onSelectionChange, 
  label,
  // NEW props
  defaultValue,
  onSaveAsDefault,
  showDefaultOption = false,
}) => {
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms[]>([]);
  const [isSavingDefault, setIsSavingDefault] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // REMOVED: const savedPaymentTerms = localStorage.getItem('paymentTerms')
  
  const fetchData = async () => {
    const result = await fetchPaymentTerms();
    setPaymentTerms(result);
  };
  
  useEffect(() => {
    fetchData();
  }, []);

  // NEW: Set default value on initial load (only for create mode)
  useEffect(() => {
    if (hasInitialized) return;
    
    // Priority 1: selectedValue (edit mode or explicit value)
    if (selectedValue) {
      setHasInitialized(true);
      return;
    }

    // Priority 2: defaultValue from centralized defaults (create mode)
    if (defaultValue && !selectedValue) {
      onSelectionChange(defaultValue);
      setHasInitialized(true);
    }
    // If neither value is available yet, don't mark initialized — wait for defaultValue to load
  }, [defaultValue, selectedValue, hasInitialized, onSelectionChange]);

  const paymentTermOptions: OptionType[] = paymentTerms.map(paymentTerm => ({
    value: paymentTerm.paymentTermId,
    label: `${paymentTerm.name}`,
  }));

  // REMOVED: useEffect for savedPaymentTerms

  const handleSelectionChange = (selectedOption: OptionType | null) => {
    if (selectedOption?.value) {
      onSelectionChange(selectedOption?.value);
      if (onSaveAsDefault) {
        onSaveAsDefault(selectedOption.value);
      }
    }
    // REMOVED: localStorage logic
  };

  // NEW: Handler for saving as default
  const handleSaveAsDefault = async () => {
    if (!onSaveAsDefault || !selectedValue) return;
    
    setIsSavingDefault(true);
    await onSaveAsDefault(selectedValue);
    setIsSavingDefault(false);
  };

  // NEW: Check if current value matches default
  const isDefaultApplied = selectedValue === defaultValue && defaultValue !== undefined;

  // NEW: Get the current payment term name for display
  const currentPaymentTerm = paymentTerms.find(pt => pt.paymentTermId === selectedValue);

  return (
    <div>
      {/* NEW: Label with optional default indicator */}
      {label && showDefaultOption && onSaveAsDefault && selectedValue && (
        <div className="d-flex w-100 justify-content-between align-items-center mb-2">
          <label className="form-label mb-0">{label}</label>
          <div
            className={`cursor-pointer d-flex align-items-center ${isSavingDefault ? 'opacity-50' : ''}`}
            onClick={handleSaveAsDefault}
            style={{ fontSize: '0.85rem' }}
          >
            {isDefaultApplied ? (
              <>
                <span className="text-muted">{'Default applied'.toTranslatedDisplay()}</span>
                <i
                  className="ms-1 bi bi-check-circle-fill text-success"
                  data-bs-toggle="tooltip"
                  title={'This payment term matches the default'.toTranslatedDisplay()}
                ></i>
              </>
            ) : (
              <>
                <span className="text-primary">{'Keep as default?'.toTranslatedDisplay()}</span>
                <i
                  className="ms-1 bi bi-check-circle text-primary"
                  data-bs-toggle="tooltip"
                  title={'Click to set as default payment term'.toTranslatedDisplay()}
                ></i>
              </>
            )}
          </div>
        </div>
      )}
      
      <Picker 
        label={!showDefaultOption ? label : undefined} // Only show label if not already shown above
        options={paymentTermOptions} 
        value={selectedValue || ''} 
        placeholder={'Select Payment Term'.toTranslatedDisplay()} 
        onSelectionChange={handleSelectionChange} 
      />
    </div>
  );
};

export const PaymentTermsList: React.FC = () => {
  const [paymentTermsList, setPaymentTermsList] = useState<PaymentTerms[]>([]);
  const [paymentTermForEdit, setPaymentTermForEdit] = useState<PaymentTerms>();
  const { updateToolbar } = useContext(ToolbarContext);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const updateButtons = () => {
    updateToolbar({
      ...defaultToolbarState,
      primaryButton: {
        enabled: true,
        text: 'Create Payment Term',
        icon: 'create', // Replace with appropriate icon type
        action: async () => {
          setPaymentTermForEdit(undefined);
          setIsEditMode(false);
          setIsModalOpen(true);
        }
      }
    });
  };

  useEffect(() => {
    updateButtons();
    const getPaymentTerms = async () => {
      const paymentTermsListResult = await fetchPaymentTerms();
      setPaymentTermsList(paymentTermsListResult);
    };
    getPaymentTerms();
  }, [triggerRefresh,isModalOpen]);

  const handleEditPaymentTerm = async (paymentTerm: PaymentTerms) => {
    setPaymentTermForEdit(paymentTerm);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDeletePaymentTerm = async (paymentTermId: string) => {
    try {
      const result = await deletePaymentTerm(paymentTermId); // Call the API handler
      toast.success(result || 'Payment term deleted successfully!'.toTranslatedDisplay()); // Show success toast
      setTriggerRefresh(!triggerRefresh)
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete payment term.'.toTranslatedDisplay()); // Show error toast
    }
  };

  const columns: Column<PaymentTerms>[] = React.useMemo(
    () => [
      {
        Header: 'Name'.toTranslatedDisplay(),
        accessor: 'name',
        size: 'small'
      },
      {
        Header: 'Days'.toTranslatedDisplay(),
        accessor: 'days',
        size: 'small'
      },
      {
        Header: 'Actions'.toTranslatedDisplay(),
        disableSortBy: true,
        width: '15%',
        ClassName: 'text-center',
        Cell: ({ row }: { row: { original: PaymentTerms } }) => (
          <div>
            <button
              onClick={() => {
                handleEditPaymentTerm(row.original);
              }}
              className="btn btn-sm btn-light p-2 m-1"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title={'Edit'.toTranslatedDisplay()}
            >
              <KTIcon className="fs-3 text-hover-primary text-muted" iconType="solid" iconName="pencil" />
            </button>
            <button onClick={() => handleDeletePaymentTerm(row.original.paymentTermId||'')} className="btn btn-sm btn-light p-2 m-1" data-bs-toggle="tooltip" data-bs-placement="top" title={'Delete'.toTranslatedDisplay()}>
              <KTIcon className="fs-3 text-muted text-hover-danger" iconName='trash' />
            </button>
          </div>
        )
      }
    ],
    []
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    // Implement the submit logic here
  };

  function handleAddNew(newOption: PaymentTerms): void {
    setIsModalOpen(false);
    setTriggerRefresh(true);
  }

  return (
    <Content>
      <DataTable columns={columns} data={paymentTermsList}
        emptyStateActions={[
          { label: 'Create Payment Term'.toTranslatedDisplay(), onClick: () => { setPaymentTermForEdit(undefined); setIsEditMode(false); setIsModalOpen(true); } },
        ]} />
      <CustomModal
        title={`${isEditMode ? 'Edit'.toTranslatedDisplay() : 'Add'.toTranslatedDisplay()} ${'Payment Term'.toTranslatedDisplay()}`}
        isVisible={isModalOpen}
        onClose={handleModalClose}
        body={
         <PaymentTermsForm onAddNew={function (newOption: PaymentTerms): void {
            setIsModalOpen(false)
          } }
          existingValue={paymentTermForEdit} />
        }
        id=""
        
        onSave={() => {setIsModalOpen(false)}}
      />
    </Content>
  );
};

interface PaymentTermsFormProps {
  onAddNew: (newOption: PaymentTerms) => void;
  existingValue?: PaymentTerms;
}

export function PaymentTermsForm({ onAddNew, existingValue }: PaymentTermsFormProps) {
  const [paymentTerm, setPaymentTerm] = useState<PaymentTerms>(new PaymentTerms());

  useEffect(() => {
    if(existingValue) setPaymentTerm(existingValue);
  }, [existingValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPaymentTerm(prevPaymentTerm => ({
      ...prevPaymentTerm,
      [name]: name === 'days' ? Number(value) : value // Ensure 'days' is handled as a number
    }));
  };



  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const updatedPaymentTerm = await upSertPaymentTerm(paymentTerm);
      toast.success('Payment Term saved successfully!'.toTranslatedDisplay());
      onAddNew(updatedPaymentTerm);
    } catch (error) {
      toast.error(`${'Error'.toTranslatedDisplay()}: ${error}`);
      console.error('Error upserting payment term:', error);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">{'Payment Term Name'.toTranslatedDisplay()}</label>
          <input
            type="text"
            className="form-control"
            placeholder={'Enter payment term name'.toTranslatedDisplay()}
            name="name"
            value={paymentTerm.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">{'Days'.toTranslatedDisplay()}</label>
          <input
            type="number"
            className="form-control"
            placeholder={'Enter number of days'.toTranslatedDisplay()}
            name="days"
            value={paymentTerm.days}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">{'Description'.toTranslatedDisplay()}</label>
          <input
            type="text"
            className="form-control"
            placeholder={'Enter description (Optional)'.toTranslatedDisplay()}
            name="description"
            value={paymentTerm.description || ''}
            onChange={handleChange}
          />
        </div>


        <button type="submit" className="btn btn-primary">{'Save'.toTranslatedDisplay()}</button>
      </form>
    </div>
  );
}
